import { useState, useEffect, useCallback } from 'react';
import initWasm, { readParquet } from 'parquet-wasm';
import { tableFromIPC } from 'apache-arrow';
import { getNodeById} from "../api/quontxApi";

/**
 * Reusable Parquet Table Component
 *
 * Handles:
 * - Parquet file parsing
 * - Sorting (with default column support)
 * - Filtering
 * - Pagination
 * - UUID resolution for headers
 * - Column visibility
 * - Time bucket formatting
 * - Number formatting
 *
 * @param {Object} props
 * @param {Function} props.fetchData - Function that returns parquet data (async)
 * @param {Object} props.headerNameMap - Map of column names to display names
 * @param {Array<string>} props.columnsToHide - Array of columns to hide
 * @param {string} props.defaultSortedColumn - Default column to sort by
 * @param {Object} props.customCellRenderers - Custom renderers for specific columns (optional)
 * @param {boolean} props.shouldLoad - Trigger to load data
 */
const ParquetTable = ({
  fetchData,
  headerNameMap = {},
  columnsToHide = [],
  defaultSortedColumn = null,
  customCellRenderers = {},
  shouldLoad = false,
  onDataLoaded = null,
}) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [headerMap, setHeaderMap] = useState({});
  const [headerUrls, setHeaderUrls] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Helper: Check if column should be hidden
  const shouldHideColumn = useCallback((columnName) => {
    return columnsToHide.some(pattern => {
      if (pattern.endsWith('*')) {
        const prefix = pattern.slice(0, -1);
        return columnName.startsWith(prefix);
      }
      return columnName === pattern;
    });
  }, [columnsToHide]);

  // Helper: Format time bucket
  const formatTimeBucket = useCallback((timeBucket) => {
    if (!timeBucket || typeof timeBucket !== 'string') return timeBucket;
    if (!timeBucket.includes('/')) return timeBucket;

    const [start, end] = timeBucket.split('/');

    try {
      const startDate = new Date(start);
      const endDate = new Date(end);

      const startMonth = startDate.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
      const startYear = startDate.getUTCFullYear();
      const endMonth = endDate.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
      const endYear = endDate.getUTCFullYear();

      if (startYear === endYear) {
        return `${startMonth} - ${endMonth} ${startYear}`;
      }

      return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
    } catch (err) {
      console.error('Error formatting time bucket:', err);
      return timeBucket;
    }
  }, []);

  // Helper: UUID Resolution
  const resolveUuid = useCallback(async (uuid) => {
    try {
      const response = await getNodeById(uuid);
      return {
        name: response.data.name || uuid,
        url: response.data.url || ''
      };
    } catch (err) {
      console.error(`Error resolving UUID ${uuid}:`, err);
      return { name: uuid, url: '' };
    }
  }, []);

  // Helper: Process header (resolve UUIDs)
  const processHeader = useCallback(async (header) => {
    if (headerNameMap[header]) {
      return { name: headerNameMap[header], url: '' };
    }

    if (header.includes('|') && resolveUuid) {
      const parts = header.split('|');
      const uuid = parts[parts.length - 1].trim();
      const result = await resolveUuid(uuid);
      return result;
    }

    return { name: header, url: '' };
  }, [headerNameMap, resolveUuid]);


  // Load and process parquet data
  const loadData = useCallback(async () => {
    if (!shouldLoad) return;

    try {
      setLoading(true);
      setError(null);
      await initWasm();

      const response = await fetchData();
      const arrayBuffer = response.data;
      const parquetData = readParquet(new Uint8Array(arrayBuffer));
      const table = tableFromIPC(parquetData.intoIPCStream());

      const jsonData = table.toArray().map(row => {
        const rowData = row.toJSON();
        const roundedRow = {};
        Object.keys(rowData).forEach(key => {
          const value = rowData[key];
          if (typeof value === 'number' && !Number.isInteger(value)) {
            roundedRow[key] = Math.round(value * 100) / 100;
          } else {
            roundedRow[key] = value;
          }
        });
        return roundedRow;
      });

      // Get visible columns
      const allColumns = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];

      // Debug

      console.log("All columns are: ", allColumns)

      const visibleCols = allColumns.filter(col => !shouldHideColumn(col));

      // Determine sort column
      const lastColumn = visibleCols[visibleCols.length - 1];
      const sortColumn = (defaultSortedColumn && visibleCols.includes(defaultSortedColumn))
        ? defaultSortedColumn
        : lastColumn;

      // Sort data descending by default
      const sortedData = [...jsonData].sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];

        if (typeof aVal === 'bigint') aVal = Number(aVal);
        if (typeof bVal === 'bigint') bVal = Number(bVal);

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'string') {
          return bVal.localeCompare(aVal);
        }

        return bVal - aVal;
      });

      setData(jsonData);
      setFilteredData(sortedData);
      setSortConfig({ key: sortColumn, direction: 'desc' });

      // Process headers
      if (jsonData.length > 0) {
        const columns = Object.keys(jsonData[0]);
        const resolvedHeaders = {};
        const resolvedUrls = {};

        await Promise.all(
          columns.map(async (col) => {
            const result = await processHeader(col);
            resolvedHeaders[col] = result.name;
            resolvedUrls[col] = result.url;
          })
        );

        setHeaderMap(resolvedHeaders);
        setHeaderUrls(resolvedUrls);
      }

      // Callback with loaded data
      if (onDataLoaded) {
        onDataLoaded(jsonData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading parquet data:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [shouldLoad, fetchData, shouldHideColumn, defaultSortedColumn, processHeader, onDataLoaded]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Sorting
  const handleSort = useCallback((key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sorted = [...filteredData].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      if (typeof aVal === 'bigint') aVal = Number(aVal);
      if (typeof bVal === 'bigint') bVal = Number(bVal);

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string') {
        return direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    setFilteredData(sorted);
    setSortConfig({ key, direction });
    setCurrentPage(1);
  }, [filteredData, sortConfig]);

  // Filtering
  const handleFilter = useCallback((column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);

    let filtered = data;
    Object.keys(newFilters).forEach(col => {
      const filterValue = newFilters[col].toLowerCase();
      if (filterValue) {
        filtered = filtered.filter(row =>
          row[col]?.toString().toLowerCase().includes(filterValue)
        );
      }
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [data, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  }, [totalPages]);

  const handleItemsPerPageChange = useCallback((e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }, []);

  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  // Render cell value
  const renderCellValue = useCallback((col, value, row) => {
    // Custom renderer if provided
    if (customCellRenderers[col]) {
      return customCellRenderers[col](value, row);
    }

    // Time bucket formatting
    if (typeof value === 'string' && value.includes('/') && value.includes('T')) {
      return formatTimeBucket(value);
    }

    // BigInt
    if (typeof value === 'bigint') {
      return value.toString();
    }

    // Decimals
    if (typeof value === 'number' && !Number.isInteger(value)) {
      return value.toFixed(2);
    }

    // Default
    return value?.toString() || '';
  }, [customCellRenderers, formatTimeBucket]);

  // Get visible columns
  const columns = data.length > 0 ? Object.keys(data[0]) : [];
  const visibleColumns = columns.filter(col => !shouldHideColumn(col));

  // Render
  return (
    <div className="w-full">
      {loading && <p className="text-white">Loading table data...</p>}
      {error && <p className="text-red-500">Error: {error}</p>}

      {!loading && !error && data.length === 0 && (
        <p className="text-sm opacity-70 text-center">
          No data available.
        </p>
      )}

      {!loading && !error && data.length > 0 && (
        <>
          {/* Pagination controls - Top */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <div style={{ color: '#FFFFFF' }}>
              Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} rows
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ color: '#FFFFFF' }}>Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={{
                  padding: '5px 10px',
                  border: '1px solid #ccc',
                  borderRadius: '3px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#000000'
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
            }}>
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col} style={{
                      border: '1px solid #ddd',
                      padding: '8px',
                      backgroundColor: '#1E40AF',
                      textAlign: 'left',
                      color: '#FFFFFF',
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontSize: '12px',
                      width: `${100 / visibleColumns.length}%`,
                    }}>
                      <div
                        onClick={() => handleSort(col)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          marginRight: '8px',
                          flex: 1,
                        }}>
                          {headerUrls[col] ? (
                            <a href={headerUrls[col]}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#FFFFFF',
                                textDecoration: 'underline'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {headerMap[col] || col}
                            </a>
                          ) : (
                            headerMap[col] || col
                          )}
                        </span>
                        {sortConfig.key === col && (
                          <span style={{ flexShrink: 0 }}>
                            {sortConfig.direction === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder={`Filter ${headerMap[col] || col}...`}
                        value={filters[col] || ''}
                        onChange={(e) => handleFilter(col, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '100%',
                          marginTop: '5px',
                          padding: '4px',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          fontSize: '12px',
                          boxSizing: 'border-box',
                          color: '#000000',
                        }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentData.map((row, idx) => (
                  <tr key={idx}>
                    {visibleColumns.map(col => (
                      <td key={col} style={{
                        border: '1px solid #ddd',
                        padding: '4px',
                        fontSize: '12px',
                        wordWrap: 'break-word',
                        overflow: 'hidden',
                      }}>
                        {renderCellValue(col, row[col], row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination controls - Bottom */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
            gap: '5px'
          }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '3px',
                backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                color: currentPage === 1 ? '#ccc' : '#333'
              }}
            >
              Previous
            </button>

            {getPageNumbers().map((page, idx) => (
              page === '...' ? (
                <span key={`ellipsis-${idx}`} style={{ padding: '8px 4px', color: '#FFFFFF' }}>...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    backgroundColor: currentPage === page ? '#1E40AF' : '#fff',
                    color: currentPage === page ? '#fff' : '#333',
                    cursor: 'pointer',
                    fontWeight: currentPage === page ? 'bold' : 'normal'
                  }}
                >
                  {page}
                </button>
              )
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '3px',
                backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                color: currentPage === totalPages ? '#ccc' : '#333'
              }}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ParquetTable;